import {
  FilledInputProps,
  inputBaseClasses,
  InputProps,
  OutlinedInputProps,
  SxProps,
  TextField,
  Theme,
} from '@mui/material';
import React, { FocusEvent, ForwardedRef, forwardRef, useEffect, useState } from 'react';
import { NumericFormat } from 'react-number-format';

export interface HBNumericFieldProps {
  allowNegative?: boolean;
  separator?: string;
  placeholder?: string;
  fullWidth?: boolean;
  required?: boolean;
  label?: string;
  size?: 'small' | 'medium';
  style?: React.CSSProperties;
  sx?: SxProps<Theme>;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  error?: boolean;
  helperText?: string;
  readOnly?: boolean;
  disabled?: boolean;
  InputProps?:
    | Partial<InputProps>
    | Partial<FilledInputProps>
    | Partial<OutlinedInputProps>
    | undefined;
}

interface WithLeadingZeros extends HBNumericFieldProps {
  allowLeadingZeros?: false;
  onChange?: (value?: number) => void;
  onEnter?: (value?: number) => void;
  onBlurInput?: () => void;
  onEscape?: () => void;
  value?: number;
}
interface WithoutLeadingZeros extends HBNumericFieldProps {
  allowLeadingZeros: true;
  onChange?: (value?: string) => void;
  onEnter?: (value?: number) => void;
  onBlurInput?: () => void;
  onEscape?: () => void;
  value?: string | number;
}

export const HBNumericField = forwardRef(
  <T extends HTMLElement>(
    {
      separator = ',',
      size = 'small',
      placeholder = '',
      fullWidth = true,
      onBlur,
      value: defaultValue,
      error,
      helperText,
      readOnly,
      disabled,
      InputProps,
      ...props
    }: WithoutLeadingZeros | WithLeadingZeros,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ref: ForwardedRef<T>,
  ) => {
    const { allowLeadingZeros, onChange, onEnter, onBlurInput, onEscape, ...restProps } = props;
    const [value, setValue] = useState<string | number | undefined>(defaultValue);

    useEffect(() => {
      setValue(defaultValue);
    }, [defaultValue]);

    return (
      <NumericFormat
        {...{
          placeholder,
          onBlur,
          defaultValue,
          error,
          helperText,
          readOnly,
          disabled,
          InputProps,
          allowLeadingZeros,
        }}
        onValueChange={(values) => {
          if (allowLeadingZeros === true) {
            onChange?.(values?.value);
            setValue(values?.value);
          } else {
            onChange?.(values?.floatValue);
            setValue(values?.floatValue);
          }
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            onEnter?.(Number(value));
          }
          if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            onEscape?.();
          }
        }}
        onBlur={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onBlurInput?.();
        }}
        fullWidth={fullWidth}
        thousandSeparator={separator}
        customInput={TextField}
        size={size}
        value={value ?? ''}
        {...restProps}
        sx={{
          [`& .${inputBaseClasses.root}`]: {
            borderRadius: 2,
          },
          ...restProps.sx,
        }}
      />
    );
  },
);

HBNumericField.displayName = 'HBNumericField';
